import { motion } from 'framer-motion';
import { Cookie, Settings, Info } from 'lucide-react';

export function PoliticaCookies() {
  return (
    <div className="min-h-screen bg-gray-950 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/10 flex items-center justify-center">
            <Cookie className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Política de Cookies
          </h1>
          <p className="text-gray-400">
            Entenda como utilizamos cookies em nosso site
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-invert prose-orange max-w-none"
        >
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">O que são Cookies?</h2>
            <p className="text-gray-300 leading-relaxed">
              Cookies são pequenos arquivos de texto que são armazenados em seu computador ou 
              dispositivo móvel quando você visita um site. Eles são amplamente utilizados para 
              fazer os sites funcionarem de forma mais eficiente, bem como para fornecer 
              informações aos proprietários do site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Como Usamos os Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Utilizamos cookies pelos seguintes motivos:
            </p>
            
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-xl p-6 border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <Settings className="w-6 h-6 text-green-500" />
                  <h3 className="text-lg font-semibold text-white">Cookies Essenciais</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Necessários para o funcionamento básico do site. Permitem navegação e uso 
                  das funcionalidades essenciais, como acesso a áreas seguras e carrinho de compras.
                  Não podem ser desativados.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <Info className="w-6 h-6 text-blue-500" />
                  <h3 className="text-lg font-semibold text-white">Cookies de Preferências</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Permitem que o site lembre suas preferências (como idioma, região) para 
                  proporcionar uma experiência personalizada e aprimorada.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <svg className="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-white">Cookies de Análise</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Nos ajudam a entender como os visitantes interagem com o site, coletando 
                  e relatando informações anonimamente. Utilizamos Google Analytics para 
                  este propósito.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-white">Cookies de Publicidade</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Utilizados para exibir anúncios relevantes aos seus interesses. Também 
                  limitam a quantidade de vezes que você vê um anúncio e nos ajudam a 
                  medir a eficácia das campanhas publicitárias. Utilizamos Google AdSense.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Cookies de Terceiros</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Além dos nossos próprios cookies, trabalhamos com os seguintes parceiros 
              que também podem configurar cookies:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>
                <strong>Google Analytics:</strong> para análise de tráfego e comportamento dos usuários;
              </li>
              <li>
                <strong>Google AdSense:</strong> para exibição de anúncios personalizados;
              </li>
              <li>
                <strong>Redes Sociais:</strong> botões de compartilhamento e integrações.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Gerenciamento de Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Você pode gerenciar suas preferências de cookies das seguintes formas:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>
                <strong>Banner de Consentimento:</strong> Ao acessar o site, você pode 
                escolher quais categorias de cookies aceitar;
              </li>
              <li>
                <strong>Configurações do Navegador:</strong> A maioria dos navegadores permite 
                que você controle cookies através das configurações de preferência;
              </li>
              <li>
                <strong>Ferramentas de Opt-out:</strong> Para cookies de publicidade, você 
                pode usar ferramentas como o{' '}
                <a 
                  href="https://optout.aboutads.info" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:underline"
                >
                  About Ads
                </a>.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Impacto da Desativação</h2>
            <p className="text-gray-300 leading-relaxed">
              Se você optar por desativar determinados cookies, observe que algumas 
              funcionalidades do site podem não funcionar corretamente. Os cookies 
              essenciais não podem ser desativados, pois são necessários para o 
              funcionamento básico do site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Alterações nesta Política</h2>
            <p className="text-gray-300 leading-relaxed">
              Podemos atualizar esta política de cookies periodicamente para refletir 
              mudanças nas tecnologias que utilizamos ou para atender a requisitos 
              regulatórios. Recomendamos que você revise esta página regularmente.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Contato</h2>
            <p className="text-gray-300 leading-relaxed">
              Se tiver dúvidas sobre nossa política de cookies, entre em contato conosco 
              através do e-mail:{' '}
              <a href="mailto:privacidade@fort3d.com.br" className="text-orange-400 hover:underline">
                privacidade@fort3d.com.br
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
